import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, HStack, VStack, Input, Select, Box, Text, Badge,
  Flex, Spinner, Checkbox, Wrap, WrapItem, Icon, IconButton, SimpleGrid,
  Tooltip, Image, Divider, useToast,
} from '@chakra-ui/react';
import { FiCalendar, FiMapPin, FiLink, FiChevronLeft, FiChevronRight, FiGrid, FiColumns, FiCopy, FiDownload, FiCheckCircle, FiExternalLink } from 'react-icons/fi';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

const PRIORITIES = [
  { value: 'critical', label: 'Critical', color: 'red' },
  { value: 'important', label: 'Important', color: 'orange' },
  { value: 'standard', label: 'Standard', color: 'gray' },
  { value: 'save_the_date', label: 'Save the Date', color: 'blue' },
  { value: 'not_important', label: 'Not Important', color: 'gray' },
];

const priorityBadge = (p) => {
  const prio = PRIORITIES.find(x => x.value === p);
  if (!prio) return null;
  return (
    <Badge fontSize="8px" colorScheme={prio.color} variant={p === 'critical' ? 'solid' : 'subtle'}>
      {prio.label}
    </Badge>
  );
};

// Safe date parser — handles "2026-03-10" and "2026-03-10T00:00:00.000Z"
function safeDate(d) {
  if (!d) return null;
  const s = typeof d === 'string' ? d.split('T')[0] : d;
  const [y, m, day] = s.split('-').map(Number);
  return new Date(y, m - 1, day);
}

function formatDate(d, t) {
  if (!d) return '';
  const dt = safeDate(d);
  if (!dt || isNaN(dt)) return '';
  const ds = dt.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  return t ? `${ds} ${t.slice(0, 5)}` : ds;
}

function formatDateFull(d) {
  if (!d) return '';
  const dt = safeDate(d);
  if (!dt || isNaN(dt)) return '';
  return dt.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

// Group entries by date
function groupByDate(entries) {
  const map = {};
  entries.forEach(e => {
    const d = e.start_date?.split('T')[0] || 'unknown';
    if (!map[d]) map[d] = [];
    map[d].push(e);
  });
  return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
}

export default function CalendarDashboardModal({ isOpen, onClose }) {
  const today = new Date().toISOString().split('T')[0];

  const { user: authUser } = useAuth();
  const weekStartDay = authUser?.week_start_day ?? 1; // 0=Sun,1=Mon,6=Sat

  // Week offset helper: get Monday of the week containing a date
  const getWeekStart = (d) => {
    const day = d.getDay(); // 0=Sun
    // distance back to the configured week start
    const diff = (day - weekStartDay + 7) % 7;
    const start = new Date(d); start.setDate(d.getDate() - diff);
    return start;
  };
  const [selectedDate, setSelectedDate] = useState(today);
  const [period, setPeriod] = useState('day');
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Also fetch "tomorrow" and "important this month" for the overview
  const [todayEntries, setTodayEntries] = useState([]);
  const [tomorrowEntries, setTomorrowEntries] = useState([]);
  const [thisWeekEntries, setThisWeekEntries] = useState([]);
  const [nextWeekEntries, setNextWeekEntries] = useState([]);
  const [importantThisMonth, setImportantThisMonth] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [overdueInvoices, setOverdueInvoices] = useState([]);
  const [missedCritical, setMissedCritical] = useState([]);
  const [showOverview, setShowOverview] = useState(true);
  const [showPastImportant, setShowPastImportant] = useState(false);
  const [detailEntry, setDetailEntry] = useState(null); // calendar entry shown in the detail modal
  const toast = useToast();
  const [showDeletedBookings, setShowDeletedBookings] = useState(false);
  const [calMonth, setCalMonth] = useState(new Date());
  const [bookedDates, setBookedDates] = useState(new Set());
  const [calendarView, setCalendarView] = useState(null); // null=schedule, 'month', 'week'

  const fetchDashboard = useCallback(async (date, per, prios) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date, period: per });
      if (prios.length > 0) params.set('priorities', prios.join(','));
      const { data: res } = await api.get(`/calendar/dashboard?${params.toString()}`);
      setData(res);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchOverview = useCallback(async () => {
    try {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomStr = tomorrow.toISOString().split('T')[0];

      const [todayRes, tomRes, monthRes] = await Promise.all([
        api.get(`/calendar/dashboard?date=${today}&period=day`),
        api.get(`/calendar/dashboard?date=${tomStr}&period=day`),
        api.get(`/calendar/dashboard?date=${today}&period=month&priorities=critical,important,save_the_date`),
      ]);

      // Also fetch next month
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1, 1);
      const nextMonthStr = nextMonth.toISOString().split('T')[0];
      let nextMonthEntries = [];
      try {
        const nmRes = await api.get(`/calendar/dashboard?date=${nextMonthStr}&period=month&priorities=critical,important,save_the_date`);
        nextMonthEntries = nmRes.data.entries || [];
      } catch(e) {}

      setTodayEntries(todayRes.data.entries || []);
      setTomorrowEntries(tomRes.data.entries || []);
      setImportantThisMonth([...(monthRes.data.entries || []), ...nextMonthEntries]);

      // This week (Mon–Sun of current week, excluding today & tomorrow)
      try {
        const todayD = new Date(today + 'T00:00:00');
        const mon = getWeekStart(todayD);
        const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
        // start from day after tomorrow to avoid duplicates
        const dayAfterTom = new Date(todayD); dayAfterTom.setDate(todayD.getDate() + 2);
        const weekFrom = dayAfterTom > mon ? dayAfterTom : mon;
        if (weekFrom <= sun) {
          const weekFromStr = weekFrom.toISOString().split('T')[0];
          const sunStr = sun.toISOString().split('T')[0];
          const { data: twRes } = await api.get(`/calendar/dashboard?date=${weekFromStr}&period=week`);
          // filter to only dates from weekFrom to sun
          const twEntries = (twRes.entries || []).filter(e => {
            const d = (e.start_date || '').split('T')[0];
            return d >= weekFromStr && d <= sunStr;
          });
          setThisWeekEntries(twEntries);
        } else {
          setThisWeekEntries([]);
        }
      } catch(e) { setThisWeekEntries([]); }

      // Next week (Mon–Sun of next week)
      try {
        const todayD = new Date(today + 'T00:00:00');
        const nextMon = new Date(getWeekStart(todayD)); nextMon.setDate(nextMon.getDate() + 7);
        const nextSun = new Date(nextMon); nextSun.setDate(nextMon.getDate() + 6);
        const nextMonStr = nextMon.toISOString().split('T')[0];
        const nextSunStr = nextSun.toISOString().split('T')[0];
        const { data: nwRes } = await api.get(`/calendar/dashboard?date=${nextMonStr}&period=week`);
        setNextWeekEntries((nwRes.entries || []).filter(e => {
          const d = (e.start_date || '').split('T')[0];
          return d >= nextMonStr && d <= nextSunStr;
        }));
      } catch(e) { setNextWeekEntries([]); }

      // Fetch birthdays
      try {
        const { data: bdData } = await api.get('/lookups/birthdays');
        setBirthdays(bdData);
      } catch(e) {}

      // Fetch deadlines (this month + next)
      try {
        const now = new Date();
        const endOfNext = new Date(now.getFullYear(), now.getMonth() + 2, 0);
        const { data: dlData } = await api.get(`/calendar/deadlines?from=${today}&to=${endOfNext.toISOString().split('T')[0]}`);
        setDeadlines(dlData);
      } catch(e) {}

      // Fetch overdue/upcoming invoices
      try {
        const { data: invData } = await api.get('/invoices?direction=in');
        const unpaid = invData.filter(i =>
          i.pay_by_date && !['paid_in_full', 'written_off', 'quotation'].includes(i.status)
        );
        const overdue = unpaid.filter(i => i.pay_by_date && i.pay_by_date.split('T')[0] <= today);
        const upcoming = unpaid.filter(i => {
          const d = i.pay_by_date?.split('T')[0];
          return d && d > today && d <= tomStr;
        });
        setOverdueInvoices([...overdue, ...upcoming]);
      } catch(e) {}

      // Fetch missed critical dates
      try {
        const { data: mcData } = await api.get('/calendar/missed-critical');
        setMissedCritical(mcData);
      } catch(e) {}
    } catch (e) { console.error(e); }
  }, [today]);

  const fetchBookedDates = useCallback(async (baseMonth) => {
    try {
      const m0 = new Date(baseMonth.getFullYear(), baseMonth.getMonth(), 1);
      const m2 = new Date(baseMonth.getFullYear(), baseMonth.getMonth() + 3, 0);
      const from = m0.toISOString().split('T')[0];
      const to = m2.toISOString().split('T')[0];
      // Fetch 3 months of data by using month period for each
      const dates = new Set();
      for (let i = 0; i < 3; i++) {
        const mi = new Date(baseMonth.getFullYear(), baseMonth.getMonth() + i, 1);
        const dateStr = mi.toISOString().split('T')[0];
        try {
          const { data: res } = await api.get(`/calendar/dashboard?date=${dateStr}&period=month`);
          (res.entries || []).forEach(e => {
            const sd = e.start_date?.split('T')[0];
            if (sd) dates.add(sd);
            // Also mark range if end_date
            if (e.end_date) {
              const ed = e.end_date.split('T')[0];
              const start = new Date(sd + 'T12:00:00');
              const end = new Date(ed + 'T12:00:00');
              for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                dates.add(d.toISOString().split('T')[0]);
              }
            }
          });
        } catch(e) {}
      }
      setBookedDates(dates);
    } catch(e) {}
  }, []);

  useEffect(() => {
    if (calendarView === 'month') fetchBookedDates(calMonth);
  }, [calendarView, calMonth, fetchBookedDates]);

  useEffect(() => {
    if (isOpen) {
      setSelectedDate(today);
      setPeriod('day');
      setSelectedPriorities([]);
      setShowOverview(true);
      setData(null);
      setCalendarView(null);
      fetchOverview();
    }
  }, [isOpen, fetchOverview, today]);

  const handleSearch = () => {
    setShowOverview(false);
    fetchDashboard(selectedDate, period, selectedPriorities);
  };

  const handleDateNav = (dir) => {
    const d = new Date(selectedDate + 'T00:00:00');
    if (period === 'day') d.setDate(d.getDate() + dir);
    else if (period === 'week') d.setDate(d.getDate() + (7 * dir));
    else if (period === 'month') d.setMonth(d.getMonth() + dir);
    const newDate = d.toISOString().split('T')[0];
    setSelectedDate(newDate);
    setShowOverview(false);
    fetchDashboard(newDate, period, selectedPriorities);
  };

  const togglePriority = (val) => {
    setSelectedPriorities(prev =>
      prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
    );
  };

  const renderEntry = (entry) => (
    <Box key={entry.id} p={2} border="1px" borderRadius="md" mb={1}
      cursor={entry.is_resource_booking ? 'default' : 'pointer'}
      onClick={entry.is_resource_booking ? undefined : () => setDetailEntry(entry)}
      _hover={entry.is_resource_booking ? undefined : { boxShadow: 'sm', borderColor: 'blue.200' }}
      borderColor={entry.is_resource_booking ? (entry.booking_status === 'confirmed' ? 'teal.300' : 'orange.200') : 'gray.100'}
      borderLeft={entry.is_resource_booking ? '4px solid' : '1px solid'}
      borderLeftColor={entry.is_resource_booking ? (entry.booking_status === 'confirmed' ? 'teal.400' : 'orange.400') : 'gray.100'}
      bg={entry.is_resource_booking ? (entry.booking_status === 'confirmed' ? 'teal.50' : 'orange.50')
        : entry.priority === 'critical' ? 'red.50' : entry.priority === 'important' ? 'orange.50' : 'white'}>
      <Flex justify="space-between" align="flex-start">
        <Box flex={1}>
          <Flex align="center" gap={2} flexWrap="wrap">
            <Text fontSize="12px" fontWeight="600">{entry.title}</Text>
            {!!entry.all_day && <Badge fontSize="7px" colorScheme="orange">All Day</Badge>}
            {entry.is_resource_booking && (
              <Badge fontSize="7px" colorScheme={entry.booking_status === 'confirmed' ? 'teal' : 'orange'}>
                📦 {entry.booking_status === 'confirmed' ? 'Confirmed Booking' : entry.booking_status === 'tentative' ? 'Tentative' : 'Booking'}
              </Badge>
            )}
            {!entry.is_resource_booking && entry.entry_type && entry.entry_type !== 'standard' && (
              <Badge fontSize="7px" colorScheme={
                { meeting_video:'blue', meeting_in_person:'purple', studio_work:'teal',
                  location_work:'orange', annual_leave:'green', maintenance:'gray', sales:'cyan', admin:'yellow'
                }[entry.entry_type] || 'gray'}>
                {entry.entry_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </Badge>
            )}
            {!entry.is_resource_booking && priorityBadge(entry.priority)}
          </Flex>
          <HStack fontSize="10px" color="gray.500" spacing={2} mt={0.5}>
            {entry.start_time && <Text>{entry.start_time.slice(0, 5)}</Text>}
            {entry.end_date && <Text>→ {formatDate(entry.end_date, entry.end_time)}</Text>}
          </HStack>
          {entry.project_name && (
            <Text fontSize="10px" color="blue.600" mt={0.5}>
              {entry.project_name}{entry.company_name ? ` — ${entry.company_name}` : ''}
            </Text>
          )}
          {entry.description && <Text fontSize="10px" color="gray.600" mt={0.5}>{entry.description}</Text>}
          <HStack mt={1} spacing={1} flexWrap="wrap">
            {entry.location && (
              <Badge fontSize="7px" colorScheme="gray" display="flex" alignItems="center" gap={1}>
                <FiMapPin size={7} /> {entry.location}
              </Badge>
            )}
            {entry.link && (
              <Badge fontSize="7px" colorScheme="blue" cursor="pointer" display="flex" alignItems="center" gap={1}
                onClick={(e) => { e.stopPropagation(); window.open(entry.link, '_blank'); }}>
                <FiLink size={7} /> Link
              </Badge>
            )}
            {entry.is_resource_booking && entry.resources?.map((r, i) => (
              <Badge key={i} fontSize="7px" colorScheme="teal" variant="solid">{r.resource_code || r.resource_name}</Badge>
            ))}
            {entry.contacts?.map(ct => (
              <Badge key={ct.id || ct.contact_id} fontSize="7px" colorScheme="purple">{ct.first_name} {ct.last_name || ''}</Badge>
            ))}
            {entry.users?.map(u => (
              <Badge key={u.id || u.user_id} fontSize="7px" colorScheme={entry.is_annual_leave ? 'green' : entry.is_resource_booking ? 'teal' : 'blue'}>{u.display_name || u.username}</Badge>
            ))}
          </HStack>
        </Box>
      </Flex>
    </Box>
  );


  const PRIORITY_COLORS = {
    critical: '#E53E3E', important: '#DD6B20', standard: '#4A5568',
    save_the_date: '#3182CE', not_important: '#A0AEC0',
  };

  const HOURS = [];
  for (let h = 7; h <= 21; h++) HOURS.push(h);
  const HOUR_HEIGHT = 54; // px per hour slot

  const renderWeekView = (entries, dateFrom) => {
    const weekStart = safeDate(dateFrom);
    if (!weekStart) return null;
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      days.push(d);
    }
    const todayStr = new Date().toISOString().split('T')[0];

    // Separate all-day entries from timed entries
    const allDayEntries = entries.filter(e => e.all_day || !e.start_time);
    const timedEntries = entries.filter(e => !e.all_day && e.start_time);

    // Group entries by day string
    const byDay = {};
    days.forEach(d => { byDay[d.toISOString().split('T')[0]] = { allDay: [], timed: [] }; });
    allDayEntries.forEach(e => {
      const k = e.start_date?.split('T')[0];
      if (byDay[k]) byDay[k].allDay.push(e);
    });
    timedEntries.forEach(e => {
      const k = e.start_date?.split('T')[0];
      if (byDay[k]) byDay[k].timed.push(e);
    });

    const parseTime = (t) => {
      if (!t) return null;
      const [h, m] = t.split(':').map(Number);
      return h + m / 60;
    };

    const entryTooltip = (e) => {
      const parts = [e.title];
      if (e.project_name) parts.push(`Project: ${e.project_name}`);
      if (e.company_name) parts.push(`Client: ${e.company_name}`);
      if (e.start_time) parts.push(`Time: ${e.start_time.slice(0,5)}${e.end_time ? ' → ' + e.end_time.slice(0,5) : ''}`);
      if (e.entry_type && e.entry_type !== 'standard') parts.push(`Type: ${e.entry_type.replace(/_/g, ' ')}`);
      if (e.location) parts.push(`Location: ${e.location}`);
      if (e.description) parts.push(e.description.slice(0, 100));
      if (e.contacts?.length) parts.push(`Contacts: ${e.contacts.map(c => `${c.first_name} ${c.last_name||''}`).join(', ')}`);
      if (e.users?.length) parts.push(`Team: ${e.users.map(u => u.display_name || u.username || '').join(', ')}`);
      return parts.join('\n');
    };



    return (
      <Box>
        {/* All-day bar */}
        {Object.values(byDay).some(d => d.allDay.length > 0) && (
          <Flex mb={1} borderBottom="1px solid" borderColor="gray.200" pb={1}>
            <Box w="48px" flexShrink={0} />
            {days.map((d, i) => {
              const k = d.toISOString().split('T')[0];
              return (
                <Box key={`ad-${i}`} flex={1} px={0.5} minH="24px">
                  {byDay[k]?.allDay.map(e => (
                    <Tooltip key={e.id} label={entryTooltip(e)} fontSize="xs" whiteSpace="pre-wrap" maxW="280px">
                      <Box bg={PRIORITY_COLORS[e.priority] || '#4A5568'} color="white" borderRadius="3px"
                        px={1} py={0.5} mb={0.5} fontSize="8px" fontWeight="600" noOfLines={1} cursor="default">
                        {e.title}
                      </Box>
                    </Tooltip>
                  ))}
                </Box>
              );
            })}
          </Flex>
        )}

        {/* Day headers */}
        <Flex>
          <Box w="48px" flexShrink={0} />
          {days.map((d, i) => {
            const k = d.toISOString().split('T')[0];
            const isToday = k === todayStr;
            return (
              <Box key={`h-${i}`} flex={1} textAlign="center" py={1} borderBottom="2px solid"
                borderColor={isToday ? 'blue.400' : 'gray.200'} bg={isToday ? 'blue.50' : 'transparent'}>
                <Text fontSize="9px" fontWeight="700" color={isToday ? 'blue.600' : 'gray.500'} textTransform="uppercase">
                  {d.toLocaleDateString('en-GB', { weekday: 'short' })}
                </Text>
                <Text fontSize="13px" fontWeight={isToday ? '800' : '600'} color={isToday ? 'blue.600' : 'gray.700'}>
                  {d.getDate()}
                </Text>
              </Box>
            );
          })}
        </Flex>

        {/* Hour grid */}
        <Box position="relative" overflowY="auto" maxH="500px">
          <Flex>
            {/* Hour labels */}
            <Box w="48px" flexShrink={0}>
              {HOURS.map(h => (
                <Flex key={h} h={`${HOUR_HEIGHT}px`} align="flex-start" justify="flex-end" pr={1} pt={0.5}>
                  <Text fontSize="9px" color="gray.400" fontWeight="500">
                    {h === 0 ? '12am' : h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h-12}pm`}
                  </Text>
                </Flex>
              ))}
            </Box>

            {/* Day columns */}
            {days.map((d, dayIdx) => {
              const k = d.toISOString().split('T')[0];
              const isToday = k === todayStr;
              const dayTimed = byDay[k]?.timed || [];

              return (
                <Box key={`col-${dayIdx}`} flex={1} position="relative" borderLeft="1px solid" borderColor="gray.100"
                  bg={isToday ? 'blue.50' : 'transparent'}>
                  {/* Hour gridlines */}
                  {HOURS.map(h => (
                    <Box key={h} h={`${HOUR_HEIGHT}px`} borderBottom="1px solid" borderColor="gray.100" />
                  ))}

                  {/* All-day strips — one per all-day entry, stacked left-to-right */}
                  {(byDay[k]?.allDay || []).map((e, si) => {
                    const stripW = 5;
                    const totalH = HOURS.length * HOUR_HEIGHT;
                    const bg = PRIORITY_COLORS[e.priority] || '#4A5568';
                    return (
                      <Tooltip key={`strip-${e.id}`} label={entryTooltip(e)} fontSize="xs" whiteSpace="pre-wrap" maxW="280px" placement="right">
                        <Box
                          position="absolute"
                          top={0}
                          left={`${si * (stripW + 1)}px`}
                          w={`${stripW}px`}
                          h={`${totalH}px`}
                          bg={bg}
                          opacity={0.35}
                          zIndex={1}
                          cursor="default"
                          _hover={{ opacity: 0.65 }}
                          transition="opacity 0.15s"
                        />
                      </Tooltip>
                    );
                  })}

                  {/* Current time indicator */}
                  {isToday && (() => {
                    const now = new Date();
                    const nowH = now.getHours() + now.getMinutes() / 60;
                    if (nowH >= HOURS[0] && nowH <= HOURS[HOURS.length - 1] + 1) {
                      const top = (nowH - HOURS[0]) * HOUR_HEIGHT;
                      return <Box position="absolute" top={`${top}px`} left={0} right={0} h="2px" bg="red.400" zIndex={3} />;
                    }
                    return null;
                  })()}

                  {/* Timed entries — overlap-aware column layout */}
                  {(() => {
                    // Build layout: assign each entry a column slot within overlapping groups
                    const laid = dayTimed.map(e => {
                      const startH = parseTime(e.start_time);
                      const endH = e.end_time ? parseTime(e.end_time) : (startH || 0) + 1;
                      return { e, startH, endH };
                    }).filter(x => x.startH !== null && x.startH >= HOURS[0]);

                    // Sort by start time
                    laid.sort((a, b) => a.startH - b.startH);

                    // Assign columns within each overlap group
                    const cols = []; // cols[i] = endH of last event in column i
                    const layout = laid.map(item => {
                      let col = cols.findIndex(endH => endH <= item.startH);
                      if (col === -1) { col = cols.length; cols.push(item.endH); }
                      else { cols[col] = item.endH; }
                      return { ...item, col };
                    });

                    // For each entry, find how many columns are active during its time span
                    const getNumCols = (item) => {
                      const active = layout.filter(o => o.startH < item.endH && o.endH > item.startH);
                      return Math.max(...active.map(o => o.col)) + 1;
                    };

                    const GAP = 4; // px gap between columns

                    return layout.map(({ e, startH, endH, col }) => {
                      const numCols = getNumCols({ startH, endH });
                      const top = (startH - HOURS[0]) * HOUR_HEIGHT;
                      const height = Math.max(HOUR_HEIGHT * 0.5, (endH - startH) * HOUR_HEIGHT);
                      const bg = PRIORITY_COLORS[e.priority] || '#4A5568';
                      const isOverlapping = numCols > 1;

                      // Column width and position with gaps
                      const totalGap = GAP * (numCols - 1) + 4; // 4px outer margin
                      const colW = `calc((100% - ${totalGap + 4}px) / ${numCols})`;
                      const colLeft = `calc(2px + ${col} * ((100% - ${totalGap + 4}px) / ${numCols} + ${GAP}px))`;

                      // Swoosh: top of split gets a right-curving clip, bottom merges back
                      // We use a subtle border-radius and shadow to create the "swoosh" feel
                      const swooshStyle = isOverlapping ? {
                        borderTopRightRadius: col < numCols - 1 ? '0px' : '4px',
                        borderBottomRightRadius: col < numCols - 1 ? '0px' : '4px',
                        borderTopLeftRadius: col > 0 ? '0px' : '4px',
                        borderBottomLeftRadius: col > 0 ? '0px' : '4px',
                        boxShadow: col === 0 ? '2px 0 0 0 white' : col === numCols - 1 ? '-2px 0 0 0 white' : '2px 0 0 0 white, -2px 0 0 0 white',
                      } : { borderRadius: '4px' };

                      // Text placement: prefer showing below any overlap zone if space allows
                      const textTop = isOverlapping && height > 30 ? '4px' : '2px';

                      return (
                        <Tooltip key={e.id} label={entryTooltip(e)} fontSize="xs" whiteSpace="pre-wrap" maxW="280px" placement="right">
                          <Box
                            position="absolute"
                            top={`${top}px`}
                            left={colLeft}
                            w={colW}
                            h={`${height}px`}
                            bg={bg}
                            overflow="hidden"
                            cursor="default"
                            zIndex={isOverlapping ? col + 2 : 2}
                            border="1px solid"
                            borderColor={`${bg}BB`}
                            opacity={0.93}
                            _hover={{ opacity: 1, zIndex: 10, boxShadow: 'lg' }}
                            transition="all 0.15s"
                            style={swooshStyle}
                          >
                            {/* Swoosh connector lines — white vertical dividers between columns */}
                            {isOverlapping && col < numCols - 1 && (
                              <Box position="absolute" right={`-${GAP}px`} top={0} w={`${GAP}px`} h="100%"
                                bg="white" zIndex={20} />
                            )}
                            <Flex direction="column" pt={textTop} px={1} pb={1} h="100%" overflow="hidden">
                              {e.company_logo_url && height > 30 && (
                                <Box as="img" src={`/api/files/${e.company_logo_url}`}
                                  style={{ maxHeight: '12px', maxWidth: '36px', objectFit: 'contain', marginBottom: '2px' }} />
                              )}
                              <Text fontSize="9px" fontWeight="700" color="white"
                                noOfLines={height > 40 ? 2 : 1} lineHeight="1.2">
                                {e.title}
                              </Text>
                              {height > 52 && e.company_name && !e.company_logo_url && (
                                <Text fontSize="8px" color="whiteAlpha.800" noOfLines={1} mt="1px">{e.company_name}</Text>
                              )}
                              {height > 68 && e.start_time && (
                                <Text fontSize="7px" color="whiteAlpha.700" mt="auto">
                                  {e.start_time.slice(0,5)}{e.end_time ? `–${e.end_time.slice(0,5)}` : ''}
                                </Text>
                              )}
                            </Flex>
                          </Box>
                        </Tooltip>
                      );
                    });
                  })()}
                </Box>
              );
            })}
          </Flex>
        </Box>
      </Box>
    );
  };

  const renderSection = (title, entries, emptyMsg) => (
    <Box mb={4}>
      <Box bg="gray.800" color="white" px={2} py={1} borderRadius="sm" mb={2} display="inline-block">
        <Text fontSize="9px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em">{title}</Text>
      </Box>
      {entries.length === 0 ? (
        <Text fontSize="11px" color="gray.400" pl={1}>{emptyMsg}</Text>
      ) : (
        entries.map(renderEntry)
      )}
    </Box>
  );

  // ── Detail-modal helpers ────────────────────────────────────────────────
  const _pad = (n) => String(n).padStart(2, '0');
  const _isAllDay = (e) => !!(e.all_day || (!e.start_time && !e.end_time));
  const _ymd = (dateStr) => (dateStr || '').split('T')[0].replace(/-/g, '');
  const _stamp = (dateStr, timeStr) => {
    const d = _ymd(dateStr);
    if (!d) return '';
    const t = (timeStr || '00:00').slice(0, 5).replace(':', '');
    return `${d}T${t}00`;
  };
  // Compute [start, end] in calendar wire format for the given entry
  const _eventRange = (e) => {
    if (_isAllDay(e)) {
      const start = _ymd(e.start_date);
      const base = new Date(((e.end_date || e.start_date) || '').split('T')[0] + 'T00:00:00');
      base.setDate(base.getDate() + 1); // DTEND is exclusive for all-day
      const end = `${base.getFullYear()}${_pad(base.getMonth() + 1)}${_pad(base.getDate())}`;
      return { start, end, allDay: true };
    }
    const start = _stamp(e.start_date, e.start_time);
    let end;
    if (e.end_time) {
      end = _stamp(e.end_date || e.start_date, e.end_time);
    } else {
      const [hh, mm] = (e.start_time || '00:00').slice(0, 5).split(':').map(Number);
      const b = new Date((e.start_date || '').split('T')[0] + `T${_pad(hh)}:${_pad(mm)}:00`);
      b.setHours(b.getHours() + 1); // default 1-hour duration
      end = `${b.getFullYear()}${_pad(b.getMonth() + 1)}${_pad(b.getDate())}T${_pad(b.getHours())}${_pad(b.getMinutes())}00`;
    }
    return { start, end, allDay: false };
  };
  const _details = (e) => [
    e.description || '',
    e.project_name ? `Project: ${e.project_name}${e.company_name ? ` — ${e.company_name}` : ''}` : '',
    e.link ? `Link: ${e.link}` : '',
  ].filter(Boolean).join('\n');

  const buildICS = (e) => {
    const { start, end, allDay } = _eventRange(e);
    const esc = (s) => String(s || '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
    const dtStamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const lines = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//ForgeBOS//Schedule//EN', 'CALSCALE:GREGORIAN',
      'BEGIN:VEVENT',
      `UID:${e.id || Date.now()}@forgebos`,
      `DTSTAMP:${dtStamp}`,
      allDay ? `DTSTART;VALUE=DATE:${start}` : `DTSTART:${start}`,
      allDay ? `DTEND;VALUE=DATE:${end}` : `DTEND:${end}`,
      `SUMMARY:${esc(e.title)}`,
    ];
    if (e.location) lines.push(`LOCATION:${esc(e.location)}`);
    const det = _details(e);
    if (det) lines.push(`DESCRIPTION:${esc(det)}`);
    lines.push('END:VEVENT', 'END:VCALENDAR');
    return lines.join('\r\n');
  };
  const downloadICS = (e) => {
    try {
      const blob = new Blob([buildICS(e)], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(e.title || 'event').replace(/[^a-z0-9]+/gi, '_')}.ics`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { toast({ title: 'Could not generate .ics', status: 'error', duration: 2000 }); }
  };
  const googleCalUrl = (e) => {
    const { start, end } = _eventRange(e);
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: e.title || 'Event',
      dates: `${start}/${end}`,
      details: _details(e),
      location: e.location || '',
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };
  const copyAddress = async (loc) => {
    try { await navigator.clipboard.writeText(loc || ''); toast({ title: 'Address copied', status: 'success', duration: 1500 }); }
    catch (err) { toast({ title: 'Copy failed', status: 'error', duration: 1500 }); }
  };
  const changeStatus = async (e, priority) => {
    try {
      await api.patch(`/calendar/${e.id}/priority`, { priority });
      setDetailEntry(prev => (prev && prev.id === e.id) ? { ...prev, priority } : prev);
      fetchOverview();
      toast({ title: 'Status updated', status: 'success', duration: 1200 });
    } catch (err) { toast({ title: 'Failed to update status', status: 'error', duration: 2000 }); }
  };
  const toggleComplete = async (e) => {
    const next = !e.is_complete;
    try {
      await api.patch(`/calendar/${e.id}/complete`, { is_complete: next });
      setDetailEntry(prev => (prev && prev.id === e.id) ? { ...prev, is_complete: next } : prev);
      fetchOverview();
      toast({ title: next ? 'Marked complete' : 'Reopened', status: 'success', duration: 1200 });
    } catch (err) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  // Deduplicate calendar entries top-down: an entry shown in an earlier section
  // (Today → Tomorrow → This Week → Next Week) must not reappear in a later one
  // (notably "Important Dates — This Month & Next"). Keyed by id + start_date so
  // distinct occurrences of a recurring entry are each kept, but the same entry
  // on the same day is shown only once, in its topmost section.
  const _seenEntryKeys = new Set();
  const _entryKey = (e) => {
    if (!e) return null;
    const id = e.id != null ? String(e.id) : '';
    const d = e.start_date != null ? String(e.start_date).split('T')[0] : '';
    if (!id && !d) return null;
    return id + '|' + d;
  };
  const dedupeTopDown = (list) => (Array.isArray(list) ? list : []).filter(e => {
    const k = _entryKey(e);
    if (k == null) return true;
    if (_seenEntryKeys.has(k)) return false;
    _seenEntryKeys.add(k);
    return true;
  });
  const todayShown = dedupeTopDown(todayEntries);
  const tomorrowShown = dedupeTopDown(tomorrowEntries);
  const thisWeekShown = dedupeTopDown(thisWeekEntries);
  const nextWeekShown = dedupeTopDown(nextWeekEntries);
  const importantShown = dedupeTopDown(
    importantThisMonth.filter(e => {
      const d = e.start_date?.split('T')[0];
      if (d === today) return false; // already in today section
      if (!showPastImportant && d < today) return false; // hide past unless toggled
      return true;
    })
  );

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size={calendarView ? 'full' : '4xl'} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH={calendarView ? '95vh' : '90vh'} mx={calendarView ? 4 : 'auto'}>
        <ModalHeader fontSize="md">
          <Flex align="center" justify="space-between" pr={8}>
            <Flex align="center" gap={2}>
              {calendarView && (
                <IconButton icon={<FiChevronLeft />} size="xs" variant="ghost"
                  onClick={() => { setCalendarView(null); }} aria-label="Back to schedule" />
              )}
              <Icon as={FiCalendar} />
              <Text>{calendarView === 'month' ? '3-Month Calendar' : calendarView === 'week' ? 'Week View' : 'Schedule'}</Text>
            </Flex>
            {calendarView && (
              <HStack spacing={1}>
                <Button size="xs" leftIcon={<FiGrid />} variant={calendarView === 'month' ? 'solid' : 'outline'} colorScheme="blue"
                  onClick={() => { setCalendarView('month'); setCalMonth(new Date()); }}>Month</Button>
                <Button size="xs" leftIcon={<FiColumns />} variant={calendarView === 'week' ? 'solid' : 'outline'} colorScheme="blue"
                  onClick={() => { setCalendarView('week'); setPeriod('week'); setShowOverview(false); fetchDashboard(selectedDate, 'week', selectedPriorities); }}>Week</Button>
              </HStack>
            )}
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>

          {/* Schedule view (default, half width) */}
          {!calendarView && (<>

          {/* Controls */}
          <Box bg="gray.50" p={3} borderRadius="md" mb={4}>
            <HStack spacing={2} mb={2}>
              <Button size="xs" variant="ghost" onClick={() => handleDateNav(-1)}><FiChevronLeft /></Button>
              <Input size="xs" type="date" value={selectedDate} w="140px"
                onChange={(e) => setSelectedDate(e.target.value)} />
              <Button size="xs" variant="ghost" onClick={() => handleDateNav(1)}><FiChevronRight /></Button>
              <Select size="xs" value={period} w="100px" fontSize="11px"
                onChange={(e) => setPeriod(e.target.value)}>
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
              </Select>
              <Button size="xs" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                onClick={handleSearch} isLoading={loading}>
                Go
              </Button>
              <Button size="xs" variant="ghost" onClick={() => { setSelectedDate(today); setShowOverview(true); setData(null); }}>
                Today
              </Button>
            </HStack>

            {/* Status filters */}
            <Wrap spacing={1} align="center">
              {PRIORITIES.map(p => (
                <WrapItem key={p.value}>
                  <Checkbox size="sm" isChecked={selectedPriorities.includes(p.value)}
                    onChange={() => togglePriority(p.value)} colorScheme={p.color}>
                    <Text fontSize="10px">{p.label}</Text>
                  </Checkbox>
                </WrapItem>
              ))}
              {selectedPriorities.length > 0 && (
                <WrapItem>
                  <Text fontSize="10px" color="red.400" cursor="pointer" onClick={() => setSelectedPriorities([])}>
                    clear
                  </Text>
                </WrapItem>
              )}
              <WrapItem>
                <Button size="xs" variant={showPastImportant ? 'solid' : 'outline'} colorScheme="gray" fontSize="9px"
                  onClick={() => setShowPastImportant(!showPastImportant)}>
                  {showPastImportant ? 'Hide' : 'Show'} past events this month
                </Button>
              </WrapItem>
              <WrapItem>
                <Button size="xs" variant={showDeletedBookings ? 'solid' : 'outline'} colorScheme="red" fontSize="9px"
                  onClick={() => setShowDeletedBookings(!showDeletedBookings)}>
                  {showDeletedBookings ? 'Hide' : 'Show'} deleted bookings
                </Button>
              </WrapItem>
            </Wrap>
          </Box>

          {/* Overview (default view) */}
          {showOverview && (
            <>
              {renderSection(
                `Today — ${formatDateFull(today)}`,
                todayShown,
                'Nothing scheduled today'
              )}
              {renderSection(
                'Tomorrow',
                tomorrowShown,
                'Nothing scheduled tomorrow'
              )}
              {thisWeekShown.length > 0 && renderSection(
                'This Week',
                thisWeekShown,
                ''
              )}
              {nextWeekShown.length > 0 && renderSection(
                'Next Week',
                nextWeekShown,
                ''
              )}
              {renderSection(
                'Important Dates — This Month & Next',
                importantShown,
                'No important dates coming up'
              )}

              {/* Birthdays */}
              {(() => {
                const now = new Date();
                const thisMonth = now.getMonth();
                const nextMonth = (thisMonth + 1) % 12;
                const upcoming = birthdays.filter(b => {
                  if (!b.birthday) return false;
                  const bd = safeDate(b.birthday);
                  if (!bd) return false;
                  const m = bd.getMonth();
                  return m === thisMonth || m === nextMonth;
                }).sort((a, b) => {
                  const da = safeDate(a.birthday), db = safeDate(b.birthday);
                  const am = da.getMonth() * 31 + da.getDate();
                  const bm = db.getMonth() * 31 + db.getDate();
                  return am - bm;
                });
                if (upcoming.length === 0) return null;
                return (
                  <Box mb={4}>
                    <Box bg="pink.600" color="white" px={2} py={1} borderRadius="sm" mb={2} display="inline-block">
                      <Text fontSize="9px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em">
                        Birthdays Coming Up
                      </Text>
                    </Box>
                    {upcoming.map(b => {
                      const bd = safeDate(b.birthday);
                      const dayStr = bd ? bd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
                      return (
                        <Box key={b.id} p={2} border="1px" borderColor="pink.200" borderRadius="md" mb={1} bg="pink.50">
                          <Flex justify="space-between" align="center">
                            <Box>
                              <Text fontSize="12px" fontWeight="600">
                                {b.first_name} {b.last_name || ''}
                                {b.company_name && <Text as="span" color="gray.500" fontSize="10px"> — {b.company_name}</Text>}
                              </Text>
                              <Text fontSize="10px" color="gray.500">{dayStr}</Text>
                            </Box>
                            <HStack spacing={1}>
                              {b.send_birthday_wish && <Badge fontSize="7px" colorScheme="green">Auto-wish</Badge>}
                            </HStack>
                          </Flex>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })()}

              {/* Deadlines */}
              {deadlines.length > 0 && (
                <Box mb={4}>
                  <Box bg="purple.700" color="white" px={2} py={1} borderRadius="sm" mb={2} display="inline-block">
                    <Text fontSize="9px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em">
                      Upcoming Deadlines
                    </Text>
                  </Box>
                  {deadlines.map((dl, i) => (
                    <Box key={`dl-${i}`} p={2} border="1px" borderColor={dl.deadline_type === 'hard' ? 'red.300' : 'purple.200'}
                      borderRadius="md" mb={1} bg={dl.deadline_type === 'hard' ? 'red.50' : 'purple.50'}>
                      <Flex justify="space-between" align="center">
                        <Box>
                          <Flex align="center" gap={2}>
                            <Text fontSize="12px" fontWeight="600">
                              {dl.source === 'task' ? dl.task_title : dl.project_name}
                            </Text>
                            <Badge fontSize="7px" colorScheme={dl.source === 'task' ? 'blue' : 'purple'}>
                              {dl.source === 'task' ? 'Task' : 'Project'}
                            </Badge>
                            {dl.deadline_type === 'hard' && <Badge fontSize="7px" colorScheme="red">Hard</Badge>}
                          </Flex>
                          <Text fontSize="10px" color="gray.500">
                            {formatDate(dl.deadline_date)}
                            {dl.source === 'task' && dl.project_name && ` · ${dl.project_name}`}
                            {dl.company_name && ` · ${dl.company_name}`}
                          </Text>
                        </Box>
                      </Flex>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Outstanding Invoices */}
              {overdueInvoices.length > 0 && (
                <Box mb={4}>
                  <Box bg="red.700" color="white" px={2} py={1} borderRadius="sm" mb={2} display="inline-block">
                    <Text fontSize="9px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em">
                      Outstanding Payments
                    </Text>
                  </Box>
                  {overdueInvoices.map(inv => (
                    <Box key={inv.id} p={2} border="1px" borderColor="red.200" borderRadius="md" mb={1} bg="red.50">
                      <Flex justify="space-between" align="center">
                        <Box>
                          <Text fontSize="12px" fontWeight="600">
                            {inv.invoice_ref || inv.invoice_number || 'Invoice'}
                            {inv.supplier_name && ` — ${inv.supplier_name}`}
                          </Text>
                          <Text fontSize="10px" color="gray.500">
                            Pay by: {inv.pay_by_date ? safeDate(inv.pay_by_date.split('T')[0])?.toLocaleDateString('en-GB') : '—'}
                            {' · '}£{parseFloat(inv.total_gross || 0).toFixed(2)}
                          </Text>
                        </Box>
                        <Badge colorScheme={inv.pay_by_date?.split('T')[0] <= today ? 'red' : 'orange'} fontSize="8px">
                          {inv.pay_by_date?.split('T')[0] <= today ? 'OVERDUE' : 'Due Soon'}
                        </Badge>
                      </Flex>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Missed Critical Dates */}
              {missedCritical.length > 0 && (
                <Box mb={4}>
                  <Box bg="red.600" color="white" px={2} py={1} borderRadius="sm" mb={2} display="inline-block">
                    <Text fontSize="9px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em">
                      Missed Critical Dates
                    </Text>
                  </Box>
                  {missedCritical.map(mc => (
                    <Box key={mc.id} p={2} border="1px" borderColor="red.300" borderRadius="md" mb={1} bg="red.50">
                      <Flex justify="space-between" align="center">
                        <Box>
                          <Flex align="center" gap={2}>
                            <Text fontSize="12px" fontWeight="600">{mc.title}</Text>
                            <Badge fontSize="7px" colorScheme="red">{mc.priority}</Badge>
                            {mc.task_progress > 0 && <Badge fontSize="7px" colorScheme="orange">{mc.task_progress}%</Badge>}
                          </Flex>
                          <Text fontSize="10px" color="gray.500">
                            {mc.start_date ? safeDate(mc.start_date.split('T')[0])?.toLocaleDateString('en-GB') : '—'}
                            {mc.project_name && ` · ${mc.project_name}`}
                            {mc.company_name && ` · ${mc.company_name}`}
                            {mc.task_title && ` · Task: ${mc.task_title}`}
                          </Text>
                        </Box>
                        <Text fontSize="9px" color="red.500" fontWeight="600">
                          {mc.task_progress < 100 ? 'Incomplete' : ''}
                        </Text>
                      </Flex>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}


          {/* Filtered results */}
          {!showOverview && (
            loading ? (
              <Flex justify="center" py={6}><Spinner size="sm" /></Flex>
            ) : data ? (
              <>
                <Text fontSize="11px" color="gray.500" mb={2}>
                  {data.entries?.length || 0} entries from {formatDateFull(data.date_from)} to {formatDateFull(data.date_to)}
                </Text>
                {data.entries?.length === 0 ? (
                  <Text fontSize="sm" color="gray.500" textAlign="center" py={6}>
                    No calendar entries for this period.
                  </Text>
                ) : period === 'week' ? (
                  renderWeekView(data.entries, data.date_from)
                ) : (
                  groupByDate(data.entries).map(([date, entries]) => (
                    <Box key={date} mb={3}>
                      <Box bg="gray.700" color="white" px={2} py={0.5} borderRadius="sm" mb={1} display="inline-block">
                        <Text fontSize="9px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em">
                          {formatDateFull(date)}
                        </Text>
                      </Box>
                      {entries.map(renderEntry)}
                    </Box>
                  ))
                )}
              </>
            ) : null
          )}

          </>)}

          {/* Month Calendar View (full screen) */}
          {calendarView === 'month' && (
            <Box>
              <Flex justify="space-between" align="center" mb={3}>
                <IconButton icon={<FiChevronLeft />} size="sm" variant="ghost"
                  onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1))} aria-label="Prev" />
                <Text fontSize="15px" fontWeight="700">
                  {[0,1,2].map(i => {
                    const m = new Date(calMonth.getFullYear(), calMonth.getMonth() + i, 1);
                    return m.toLocaleDateString('en-GB', { month: 'long' });
                  }).join('  ·  ')}
                  {' '}{calMonth.getFullYear()}
                </Text>
                <IconButton icon={<FiChevronRight />} size="sm" variant="ghost"
                  onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1))} aria-label="Next" />
              </Flex>
              <Flex gap={6} justify="center" flexWrap="wrap">
                {[0,1,2].map(offset => {
                  const m = new Date(calMonth.getFullYear(), calMonth.getMonth() + offset, 1);
                  const year = m.getFullYear();
                  const month = m.getMonth();
                  const firstDay = new Date(year, month, 1).getDay();
                  const daysInMonth = new Date(year, month + 1, 0).getDate();
                  const startOffset = firstDay === 0 ? 6 : firstDay - 1;
                  const cells = [];
                  for (let i = 0; i < startOffset; i++) cells.push(null);
                  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
                  const todayStr = new Date().toISOString().split('T')[0];

                  return (
                    <Box key={offset} flex="1" minW="220px" maxW="300px">
                      <Text fontSize="13px" fontWeight="700" textAlign="center" mb={2}>
                        {m.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                      </Text>
                      <SimpleGrid columns={7} spacing={0}>
                        {['M','T','W','T','F','S','S'].map((d, i) => (
                          <Text key={i} fontSize="9px" fontWeight="700" textAlign="center" color="gray.400" py={1}>{d}</Text>
                        ))}
                        {cells.map((day, i) => {
                          if (!day) return <Box key={`e-${i}`} />;
                          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                          const isBooked = bookedDates.has(dateStr);
                          const isToday = dateStr === todayStr;
                          return (
                            <Flex key={i} justify="center" align="center" h="32px" cursor="pointer"
                              onClick={() => { setSelectedDate(dateStr); setPeriod('day'); setShowOverview(false); setCalendarView(null);
                                fetchDashboard(dateStr, 'day', selectedPriorities); }}
                              borderRadius="full"
                              bg={isBooked ? '#EE7203' : isToday ? 'blue.500' : 'transparent'}
                              color={isBooked || isToday ? 'white' : 'gray.700'}
                              fontWeight={isBooked || isToday ? '700' : '400'}
                              fontSize="11px"
                              _hover={{ bg: isBooked ? '#c05600' : 'blue.100' }}>
                              {day}
                            </Flex>
                          );
                        })}
                      </SimpleGrid>
                    </Box>
                  );
                })}
              </Flex>
            </Box>
          )}

          {/* Week Calendar View (full screen) */}
          {calendarView === 'week' && (
            <Box>
              <Box bg="gray.50" p={3} borderRadius="md" mb={4}>
                <HStack spacing={2}>
                  <Button size="xs" variant="ghost" onClick={() => handleDateNav(-1)}><FiChevronLeft /></Button>
                  <Input size="xs" type="date" value={selectedDate} w="140px"
                    onChange={(e) => setSelectedDate(e.target.value)} />
                  <Button size="xs" variant="ghost" onClick={() => handleDateNav(1)}><FiChevronRight /></Button>
                  <Button size="xs" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                    onClick={() => { setPeriod('week'); setShowOverview(false); fetchDashboard(selectedDate, 'week', selectedPriorities); }} isLoading={loading}>Go</Button>
                  <Button size="xs" variant="ghost" onClick={() => { setSelectedDate(today); setPeriod('week'); setShowOverview(false); fetchDashboard(today, 'week', selectedPriorities); }}>This Week</Button>
                </HStack>
              </Box>
              {loading ? (
                <Flex justify="center" py={6}><Spinner size="sm" /></Flex>
              ) : data && data.entries ? (
                renderWeekView(data.entries, data.date_from)
              ) : (
                <Text fontSize="sm" color="gray.500" textAlign="center" py={6}>Select a date and click Go</Text>
              )}
            </Box>
          )}

        </ModalBody>
        <ModalFooter justifyContent="space-between">
          <Button size="sm" onClick={onClose}>Close</Button>
          {!calendarView && (
            <HStack spacing={1}>
              <Button size="xs" leftIcon={<FiGrid />} variant="outline" colorScheme="blue"
                onClick={() => { setCalendarView('month'); setCalMonth(new Date()); fetchBookedDates(new Date()); }}>Month</Button>
              <Button size="xs" leftIcon={<FiColumns />} variant="outline" colorScheme="blue"
                onClick={() => { setCalendarView('week'); setPeriod('week'); setShowOverview(false); fetchDashboard(selectedDate, 'week', selectedPriorities); }}>Week</Button>
            </HStack>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>

    {/* Calendar entry detail modal */}
    <Modal isOpen={!!detailEntry} onClose={() => setDetailEntry(null)} size="md" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        {detailEntry && (() => {
          const e = detailEntry;
          const prio = PRIORITIES.find(p => p.value === e.priority);
          return (
            <>
              <ModalHeader fontSize="md" pr={10}>
                <Flex align="center" gap={2} flexWrap="wrap">
                  <Icon as={FiCalendar} />
                  <Text>{e.title}</Text>
                  {!!e.all_day && <Badge fontSize="8px" colorScheme="orange">All Day</Badge>}
                  {!!e.is_complete && <Badge fontSize="8px" colorScheme="green">Complete</Badge>}
                </Flex>
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack align="stretch" spacing={3}>
                  <Box fontSize="12px" color="gray.700">
                    <Text fontWeight="600">{formatDate(e.start_date, e.all_day ? null : e.start_time)}</Text>
                    {e.end_date && <Text color="gray.500">→ {formatDate(e.end_date, e.all_day ? null : e.end_time)}</Text>}
                  </Box>

                  {e.entry_type && e.entry_type !== 'standard' && (
                    <Badge w="fit-content" fontSize="8px" colorScheme="purple">
                      {e.entry_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </Badge>
                  )}

                  {e.project_name && (
                    <Text fontSize="11px" color="blue.600">{e.project_name}{e.company_name ? ` — ${e.company_name}` : ''}</Text>
                  )}
                  {e.description && <Text fontSize="12px" color="gray.700" whiteSpace="pre-wrap">{e.description}</Text>}

                  {e.location && (
                    <Box>
                      <Text fontSize="9px" color="gray.500" fontWeight="700" textTransform="uppercase" mb={1}>Location</Text>
                      <Flex align="center" gap={2}>
                        <Flex align="center" gap={1} flex={1}><Icon as={FiMapPin} color="gray.500" boxSize={3} /><Text fontSize="12px">{e.location}</Text></Flex>
                        <Button size="xs" leftIcon={<FiCopy />} variant="outline" onClick={() => copyAddress(e.location)}>Copy address</Button>
                      </Flex>
                    </Box>
                  )}

                  {e.link && (
                    <Button size="xs" leftIcon={<FiExternalLink />} variant="outline" colorScheme="blue" w="fit-content"
                      onClick={() => window.open(e.link, '_blank')}>Open link</Button>
                  )}

                  {(e.contacts?.length > 0 || e.users?.length > 0) && (
                    <Wrap spacing={1}>
                      {e.contacts?.map(ct => (
                        <WrapItem key={`c-${ct.id || ct.contact_id}`}><Badge fontSize="8px" colorScheme="purple">{ct.first_name} {ct.last_name || ''}</Badge></WrapItem>
                      ))}
                      {e.users?.map(u => (
                        <WrapItem key={`u-${u.id || u.user_id}`}><Badge fontSize="8px" colorScheme="blue">{u.display_name || u.username}</Badge></WrapItem>
                      ))}
                    </Wrap>
                  )}

                  <Divider />

                  {/* Add to calendar */}
                  <Box>
                    <Text fontSize="9px" color="gray.500" fontWeight="700" textTransform="uppercase" mb={1}>Add to calendar</Text>
                    <HStack>
                      <Button size="xs" leftIcon={<FiDownload />} variant="outline" onClick={() => downloadICS(e)}>Outlook (.ics)</Button>
                      <Button size="xs" leftIcon={<FiCalendar />} variant="outline" colorScheme="blue"
                        onClick={() => window.open(googleCalUrl(e), '_blank')}>Google Calendar</Button>
                    </HStack>
                  </Box>

                  {/* Status */}
                  <Box>
                    <Text fontSize="9px" color="gray.500" fontWeight="700" textTransform="uppercase" mb={1}>Status</Text>
                    <HStack>
                      <Select size="xs" maxW="180px" value={e.priority || 'standard'} onChange={(ev) => changeStatus(e, ev.target.value)}>
                        {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </Select>
                      <Button size="xs" leftIcon={<FiCheckCircle />} colorScheme={e.is_complete ? 'gray' : 'green'}
                        variant={e.is_complete ? 'outline' : 'solid'} onClick={() => toggleComplete(e)}>
                        {e.is_complete ? 'Reopen' : 'Mark complete'}
                      </Button>
                    </HStack>
                  </Box>
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button size="sm" onClick={() => setDetailEntry(null)}>Close</Button>
              </ModalFooter>
            </>
          );
        })()}
      </ModalContent>
    </Modal>
    </>
  );
}
